import React, { useState, useEffect, useMemo } from 'react';
import api from '../../config/axiosConfig';
import UserGroupContext from '../../contexts/UserGroupContext';
import { 
  Box, Typography, Container, Grid, Paper, Card, CardContent, 
  Chip, Avatar, Divider, CircularProgress, Button, Tooltip, 
  List, ListItem, ListItemText, ListItemAvatar, Collapse,
  TextField, IconButton, Badge, Switch, FormControlLabel
} from '@mui/material';
import styles from './css/ContactRecord.module.css';
import { 
  Autorenew, Error, KeyboardArrowDown, KeyboardArrowUp, 
  Search, QuestionAnswer, Notifications, NotificationsActive
} from '@mui/icons-material';

function ContactRecord() {
  const userGroups = React.useContext(UserGroupContext);
  const [recordsList, setRecordsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [showOnlyUnreplied, setShowOnlyUnreplied] = useState(false);
  
  useEffect(() => {
    document.title = '韓國線聯絡記錄查詢';
    fetchAllContactRecords();
  }, []);

  // 獲取所有聯絡記錄
  const fetchAllContactRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/korea/api/all-contact-records/');
      setRecordsList(response.data.records);
      setLoading(false);
    } catch (error) {
      console.error('獲取聯絡記錄失敗:', error);
      setLoading(false);
    }
  };

  // 重新獲取資料
  const handleRefresh = () => {
    fetchAllContactRecords();
  };

  // 切換展開/收合狀態
  const toggleExpand = (groupId) => {
    setExpandedItems(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // 處理搜尋文字變更
  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  // 切換只顯示未回覆項目
  const handleUnrepliedToggle = () => {
    setShowOnlyUnreplied(!showOnlyUnreplied);
  };

  // 篩選後的資料
  const filteredRecords = useMemo(() => {
    return recordsList.filter(record => {
      // 檢查是否符合搜尋文字
      const matchesSearch = searchText === '' || 
        record.group_number.toLowerCase().includes(searchText.toLowerCase());
      
      // 檢查是否符合未回覆過濾
      let matchesUnrepliedFilter = true;
      if (showOnlyUnreplied) {
        const hasUnreplied = record.external_records.some(er => !er.reply) || 
                            record.internal_records.some(ir => !ir.reply);
        matchesUnrepliedFilter = hasUnreplied;
      }
      
      return matchesSearch && matchesUnrepliedFilter;
    });
  }, [recordsList, searchText, showOnlyUnreplied]);

  // 計算未回覆數量的輔助函數
  const getUnrepliedCount = (record) => {
    const externalUnreplied = record.external_records.filter(r => !r.reply).length;
    const internalUnreplied = record.internal_records.filter(r => !r.reply).length;
    return externalUnreplied + internalUnreplied;
  };

  // 計算總聯絡數量
  const getTotalContactCount = (record) => {
    return record.external_records.length + record.internal_records.length;
  };

  return (
    <Container component="main" maxWidth="lg" sx={{ py: 3 }}>
      <Typography className={styles['text-h1']} variant="h5" gutterBottom>
        韓 國 線 聯 絡 記 錄 查 詢
      </Typography>

      {/* 搜尋和過濾工具列 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="搜尋團號"
            size="small"
            variant="outlined"
            value={searchText}
            onChange={handleSearchTextChange}
            InputProps={{
              startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={showOnlyUnreplied} 
                onChange={handleUnrepliedToggle}
                color="error"
              />
            }
            label="僅顯示未回覆"
          />
        </Box>

        <Tooltip title="重新整理資料" placement="top">
          <IconButton 
            onClick={handleRefresh} 
            color="primary" 
            sx={{ border: '1px solid', borderRadius: '4px' }}
          >
            <Autorenew />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 主要內容區域 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredRecords.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography className={styles['no-record']} variant="h6">
            沒有找到符合條件的聯絡記錄
          </Typography>
        </Paper>
      ) : (
        <List sx={{ width: '100%', bgcolor: 'background.paper' }} className={styles['records-list']}>
          {filteredRecords.map((record) => {
            const unrepliedCount = getUnrepliedCount(record);
            const totalCount = getTotalContactCount(record);
            const isExpanded = expandedItems[record.uuid] || false;
            
            return (
              <Paper key={record.uuid} elevation={3} sx={{ mb: 2, overflow: 'hidden' }}>
                <ListItem 
                  alignItems="flex-start" 
                  button 
                  onClick={() => toggleExpand(record.uuid)}
                  className={styles['list-item']}
                  sx={{ 
                    bgcolor: unrepliedCount > 0 ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                    borderLeft: unrepliedCount > 0 ? '4px solid #f44336' : 'none'
                  }}
                >
                  <ListItemAvatar>
                    <Badge 
                      badgeContent={unrepliedCount} 
                      color="error"
                      max={99}
                      invisible={unrepliedCount === 0}
                    >
                      <Avatar sx={{ bgcolor: unrepliedCount > 0 ? '#f44336' : '#1976d2' }}>
                        {unrepliedCount > 0 ? <NotificationsActive /> : <Notifications />}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Typography variant="h6" component="div" fontWeight="bold">
                        {record.group_number}
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          總聯絡記錄: {totalCount} | 未回覆: {unrepliedCount}
                        </Typography>
                        <Typography component="div" variant="body2" color="text.secondary">
                          最後更新: {new Date(record.last_updated).toLocaleString()}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                  <Button 
                    variant="outlined" 
                    color="primary"
                    href={`/Korea/order-information/${record.uuid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<QuestionAnswer />}
                    onClick={(e) => e.stopPropagation()} 
                    >
                    查看訂單完整資訊
                  </Button>
                  
                  <IconButton disableRipple edge="end" sx={{ mr: 1, position: 'absolute', left: '50%', bottom: '0', transform: 'translateX(-50%)' }}>
                    {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>
                </ListItem>
                
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <Grid container spacing={2}>
                      {/* 外站聯絡記錄 */}
                      <Grid item xs={12} md={userGroups.includes('韓方人員') ? 12 : 6}>
                        <Typography className={styles["list-section-title"]}>
                          外站聯絡記錄 ({record.external_records.length})
                        </Typography>

                        {record.external_records.length > 0 ? (
                          record.external_records.map(externalRecord => (
                            <Card key={externalRecord.id} elevation={2} sx={{ mb: 2 }}>
                              <CardContent className={styles['record-content']}>
                                {!externalRecord.reply && (
                                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                                    <Chip
                                      icon={<Error />}
                                      label="未回覆"
                                      color="error"
                                      size="small"
                                    />
                                  </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Chip
                                    avatar={<Avatar src={externalRecord.question_user_photo} />}
                                    label={externalRecord.question_user}
                                    variant="outlined"
                                    size="medium"
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    提問時間: {new Date(externalRecord.question_created_at).toLocaleString()}
                                  </Typography>
                                </Box>
                                
                                <Typography className={styles['question-text']}>
                                  {externalRecord.question}
                                </Typography>
                                
                                {externalRecord.reply ? (
                                  <>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                        回覆時間: {new Date(externalRecord.reply_created_at).toLocaleString()}
                                      </Typography>
                                      <Chip
                                        avatar={<Avatar src={externalRecord.reply_user_photo} />}
                                        label={externalRecord.reply_user}
                                        variant="outlined"
                                        size="medium"
                                      />
                                    </Box>
                                    <Typography className={styles['reply-text']}>
                                      {externalRecord.reply}
                                    </Typography>
                                  </>
                                ) : (
                                  <Divider sx={{ my: 1.5 }}>尚未回覆</Divider>
                                )}
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <Typography className={styles["no-record-section"]}>
                            無外站聯絡記錄
                          </Typography>
                        )}
                      </Grid>

                      {/* 內站聯絡記錄 - 只有非韓方人員可見 */}
                      {!userGroups.includes('韓方人員') && (
                        <Grid item xs={12} md={6}>
                          <Typography className={styles["list-section-title-internal"]}>
                            內站聯絡記錄 ({record.internal_records.length})
                          </Typography>

                          {record.internal_records.length > 0 ? (
                            record.internal_records.map(internalRecord => (
                              <Card key={internalRecord.id} elevation={2} sx={{ mb: 2 }}>
                                <CardContent className={styles['record-content']}>
                                  {!internalRecord.reply && (
                                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                                      <Chip
                                        icon={<Error />}
                                        label="未回覆"
                                        color="error"
                                        size="small"
                                      />
                                    </Box>
                                  )}
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Chip
                                      avatar={<Avatar src={internalRecord.question_user_photo} />}
                                      label={internalRecord.question_user}
                                      variant="outlined"
                                      size="medium"
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      提問時間: {new Date(internalRecord.question_created_at).toLocaleString()}
                                    </Typography>
                                  </Box>

                                  <Typography className={styles['question-text']}>
                                    {internalRecord.question}
                                  </Typography>

                                  {internalRecord.question_target && internalRecord.question_target.length > 0 && (
                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                        提問對象:
                                      </Typography>
                                      {internalRecord.question_target.map(target => (
                                        <Tooltip key={target.id} title={target.nickname} placement="top">
                                          <Avatar
                                            alt={target.nickname}
                                            src={target.photo}
                                            sx={{ width: 24, height: 24, ml: 0.5 }}
                                          />
                                        </Tooltip>
                                      ))}
                                    </Box>
                                  )}
                                  
                                  {internalRecord.reply ? (
                                    <>
                                      <Divider sx={{ my: 1.5 }} />
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                          回覆時間: {new Date(internalRecord.reply_created_at).toLocaleString()}
                                        </Typography>
                                        <Chip
                                          avatar={<Avatar src={internalRecord.reply_user_photo} />}
                                          label={internalRecord.reply_user}
                                          variant="outlined"
                                          size="medium"
                                        />
                                      </Box>
                                      <Typography className={styles['reply-text']}>
                                        {internalRecord.reply}
                                      </Typography>
                                    </>
                                  ) : (
                                    <Divider sx={{ my: 1.5 }}>尚未回覆</Divider>
                                  )}
                                </CardContent>
                              </Card>
                            ))
                          ) : (
                            <Typography className={styles["no-record-section"]}>
                              無內站聯絡記錄
                            </Typography>
                          )}
                        </Grid>
                      )}
                    </Grid>

                    {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button 
                        variant="outlined" 
                        color="primary"
                        href={`/Korea/order-information/${record.uuid}`}
                        startIcon={<QuestionAnswer />}
                      >
                        查看訂單完整資訊
                      </Button>
                    </Box> */}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </List>
      )}
    </Container>
  );
}

export default ContactRecord;
